<template>
  <main class="table" @click="skipHold">
    <WorldScene ref="world" class="table__world" :civ="shownCiv" @pick="onPick" />
    <div v-if="caption" class="table__caption" :style="captionStyle" @click.stop="caption = null">
      <strong>{{ caption.title }}</strong>
      <span>{{ caption.text }}</span>
    </div>
    <div class="table__vignette" aria-hidden="true" />
    <!-- Each people's NAME over its home, in its colour, with its temper
         (after playtest 9: "really unclear on the map what they were"). -->
    <div
      v-for="p in plates"
      :key="p.id"
      class="people-plate"
      :class="`people-plate--${p.stance}`"
      :style="{ left: `${p.x}px`, top: `${p.y}px`, '--people': p.colour }"
      aria-hidden="true"
    ><span class="people-plate__name">{{ p.name }}</span><span class="people-plate__temper">{{ p.stance }}</span></div>
    <RolePeek v-if="peekRole" :game="game" :role="peekRole" @close="peekRole = null" />
    <!-- Which way is north (playtest 9: "I wonder which way north is"). The
         island turns; the needle turns with it. -->
    <!-- Tap it and the camera swings round until north is up (playtest 11). -->
    <button v-if="northAngle !== null" type="button" class="map-north map-north--table map-north--tap" :style="{ transform: `rotate(${northAngle}rad)` }" aria-label="North - tap to turn the island north-up" @click="$refs.world?.faceNorth?.()">
      <span class="map-north__needle" aria-hidden="true" /><span class="map-north__n">N</span>
    </button>

    <header class="table__top">
      <div class="table__round">
        <span class="table__era">{{ seasonWord }}</span>
        <span>{{ date }}</span>
      </div>
      <h2 class="table__headline">{{ headline }}</h2>
    </header>

    <!-- The deck sits in the stats row, right of threat (playtest 7: it was
         tucked behind the menu button in the corner). -->
    <ResourceStrip :civ="shownCiv" big class="table__resources" :eats="eatsTonight" @explain="explainTrack">
      <DeckStack class="table__deck" :count="shownDeckCount" />
    </ResourceStrip>

    <!-- who goes when, and what the commonwealth knows -->
    <aside class="table__side table__side--left">
      <h3 class="zone zone--table">
        <span class="zone__label">This round</span>
        <span v-if="phase === PHASES.DAY" class="zone__count" :class="{ 'zone__count--spent': !stillWorking.length }">
          {{ stillWorking.length ? `${stillWorking.length} still at work` : 'everyone is done' }}
        </span>
      </h3>
      <ul class="turn-rail turn-rail--table turn-rail--hands">
        <li
          v-for="role in ROLES"
          :key="role"
          class="turn-rail__step"
          :class="{ 'turn-rail__step--done': phase === PHASES.DAY && game.done[role], 'turn-rail__step--active': phase === PHASES.DAY && !game.done[role] }"
          :style="{ '--role': ROLE_INFO[role].colour }"
          role="button"
          tabindex="0"
          :title="`What the ${ROLE_INFO[role].label} are doing`"
          @click.stop="peekRole = role"
        >
          <span>{{ ROLE_INFO[role].label }}</span>
          <small>{{ playerName(game.roles[role]) }}</small>
          <small v-if="phase === PHASES.DAWN" class="turn-rail__need">+1: {{ asks[role] }}</small>
          <small v-else-if="game.needs?.[role]" class="turn-rail__need">needs {{ game.needs[role] }}</small>
          <small class="turn-rail__hands">
            <template v-if="phase === PHASES.DAWN">{{ allocation[role] }} {{ allocation[role] === 1 ? 'worker' : 'workers' }}</template>
            <template v-else-if="phase === PHASES.DAY">{{ game.done[role] ? 'done' : (role === 'culture' ? `${hallLeft(game.civ).playable} ${hallLeft(game.civ).playable === 1 ? 'card' : 'cards'} to play` : role === 'military' ? `${hands[role] || 0} ${tokenWord(role, hands[role] || 0)} left` : `${workersFor(role, hands[role])} left`) }}</template>
            <!-- After the day the split on file is TOMORROW's (playtest 12:
                 "it should not show us those until we actually reach" dawn). -->
            <template v-else>done for the day</template>
          </small>
        </li>
      </ul>
      <p v-if="phase === PHASES.DAWN" class="table__muted">{{ game.meta.round === 1 ? 'The first split is dealt; from tomorrow the phones decide it.' : 'The workers are being divided on the phones.' }} The four ways to win: {{ victoryLine }}.</p>
      <div v-if="demandLine" class="table__demand">
        <span class="table__demand-kicker">The people want</span>
        <strong>{{ demandLine.label }}</strong>
        <small>They will ask {{ demandLine.when }}.</small>
      </div>

      <!--
        THE PEOPLES, each one its own (Matt, after playtest 9: the other
        civilizations should "have more meaning and be much clearer and more
        distinct"). Who they are, their temper, what makes them them, and
        what they will do next - so a temper is a forecast, not a word.
      -->
      <template v-if="peoples.length || unmetPeoples">
        <h3 class="zone zone--table">
          <span class="zone__label">The peoples</span>
        </h3>
        <ul class="peoples">
          <li v-for="p in peoples" :key="p.id" class="people" :style="{ '--people': p.colour }">
            <span class="people__head">
              <span class="people__swatch" aria-hidden="true" />
              <strong class="people__name">{{ p.name }}</strong>
              <span class="people__temper" :class="`people__temper--${p.stance}`">{{ p.stance }}</span>
              <span class="people__power">strength {{ p.power }}</span>
            </span>
            <span class="people__way">{{ p.way }}</span>
            <span class="people__next">{{ p.next }}</span>
          </li>
          <li v-if="unmetPeoples" class="table__muted people__unmet">{{ unmetPeoples }} more {{ unmetPeoples === 1 ? 'people' : 'peoples' }} not yet met.</li>
        </ul>
      </template>

      <h3 class="zone zone--table">
        <span class="zone__label">The commonwealth</span>
      </h3>
      <ul class="table__facts">
        <li>
          <strong>The great work</strong>
          <span v-if="!goal" class="table__muted">{{ raisedWonders.length ? 'the next Vision is in the deck' : 'chosen when the Vision card turns' }}</span>
          <template v-else>
            <span>{{ goal.name }}</span>
            <span v-if="goal.build" class="monument">
              <span v-for="n in goal.build.stages" :key="n" class="monument__stage" :class="{ 'monument__stage--built': n <= shownCiv.monument.stages }" />
            </span>
          </template>
        </li>
        <li v-if="raisedWonders.length">
          <strong>Wonders</strong>
          <!-- What each one GIVES, on the face of it: the table cannot be
               tapped, so a name alone left everyone asking (playtest jeJqm). -->
          <span v-for="w in raisedWonders" :key="w.name" class="table__wonder"><span class="zone__count zone__count--quiet">{{ w.name }}</span> <span class="table__muted">{{ w.gives }}</span></span>
        </li>
        <!-- Counts wear the same chip the boards' headings use, rather
             than running together as prose separated by dots. -->
        <li>
          <strong>The city</strong>
          <span class="zone__count zone__count--quiet">{{ (shownCiv.city?.hexes || []).length }} hexes</span>
          <span class="zone__count zone__count--quiet">{{ shownCiv.farms }} fields</span>
          <span class="zone__count zone__count--quiet">{{ shownCiv.markets }} markets</span>
          <span class="zone__count zone__count--quiet">{{ shownCiv.libraries }} libraries</span>
        </li>
        <!-- The Hall sits ABOVE the lists that grow (playtest 6: "the
             government is scrolling off the bottom of the screen... as
             we're listing technologies over there"). -->
        <li v-if="shownCiv.hall" class="table__fact-compass">
          <strong>The Hall</strong>
          <span>{{ governmentName }}<span class="table__muted"> · {{ compassText }}</span></span>
          <Compass :civ="shownCiv" />
          <span class="table__muted">Council: {{ councilNames || 'the chairs are empty' }}</span>
        </li>
        <li>
          <strong>Known</strong>
          <span v-if="!shownCiv.techs.length" class="table__muted">nothing yet</span>
          <template v-else>
            <span class="zone__count zone__count--quiet">{{ shownCiv.techs.length }} {{ shownCiv.techs.length === 1 ? 'technology' : 'technologies' }}</span>
            <span>{{ knownRecent }}</span>
          </template>
        </li>
        <li v-if="shownCiv.lore && settledText">
          <strong>Settled</strong>
          <span>{{ settledText }}</span>
        </li>
        <!-- THE VICTORIES (DESIGN.md "The Year"): on the table from the first
             month, every one with how far off it is; the neighbours' too. -->
        <li v-for="v in victories" :key="v.id" class="table__victory">
          <strong>{{ v.name }}</strong>
          <span class="zone__count zone__count--quiet">{{ v.have }} of {{ v.need }}</span>
        </li>
        <li v-for="r in rivalRace" :key="r.id" class="table__victory table__victory--theirs" :style="{ '--people': rivalColour(r.id) }">
          <strong>{{ r.name }}</strong>
          <span>chase Legacy</span>
          <span class="zone__count zone__count--quiet">{{ r.have }} of {{ r.need }}</span>
        </li>
        <li v-if="(shownCiv.tales || []).length">
          <strong>Tales</strong>
          <span>{{ shownCiv.tales.map((id) => TALES[id]?.name || id).join(', ') }}</span>
        </li>
        <li v-if="yearGoal">
          <strong>This year</strong>
          <span>{{ yearGoal }}</span>
        </li>
        <li v-if="(shownCiv.lostWonders || []).length">
          <strong>Lost to rivals</strong>
          <span>{{ shownCiv.lostWonders.map((id) => GOALS[id]?.name || id).join(', ') }}</span>
        </li>
        <li v-if="(shownCiv.roads || []).length">
          <strong>Roads</strong>
          <span class="zone__count zone__count--quiet">{{ shownCiv.roads.length }} {{ shownCiv.roads.length === 1 ? 'hex' : 'hexes' }}</span>
        </li>
        <li v-if="(shownCiv.units || []).length || (shownCiv.menaces || []).length">
          <strong>The frontier</strong>
          <span class="zone__count zone__count--quiet">
            {{ (shownCiv.units || []).length }} {{ (shownCiv.units || []).length === 1 ? 'unit' : 'units' }}
          </span>
          <span v-if="(shownCiv.menaces || []).length" class="zone__count zone__count--bad">
            {{ shownCiv.menaces.length }} {{ shownCiv.menaces.length === 1 ? 'camp' : 'camps' }}
          </span>
          <span v-if="(shownCiv.raiders || []).length" class="zone__count zone__count--bad">
            {{ shownCiv.raiders.length }} raiding
          </span>
        </li>
        <!-- The network: what the Frontier has planted, and what it sends. -->
        <li v-if="(shownCiv.villages || []).length">
          <strong>The network</strong>
          <span v-for="v in shownCiv.villages" :key="v.id" class="zone__count" :class="v.damaged ? 'zone__count--bad' : 'zone__count--quiet'">
            {{ v.name }}{{ v.chartered ? ' (town)' : '' }}{{ v.damaged ? ' - wrecked' : '' }}
          </span>
        </li>
        <li v-if="(shownCiv.branches || []).length">
          <strong>Branches</strong>
          <span>{{ shownCiv.branches.map((b) => BRANCHES[b]?.name).filter(Boolean).join(', ') }}</span>
        </li>
        <li>
          <strong>Found</strong>
          <span v-if="!shownCiv.regions.length" class="table__muted">only home</span>
          <span v-else>{{ shownCiv.regions.map((r) => REGIONS[r].name).join(', ') }}</span>
        </li>
      </ul>
    </aside>

    <!-- the story -->
    <aside class="table__side table__side--right">
      <h3 class="zone zone--table">
        <span class="zone__label">The story so far</span>
      </h3>
      <!--
        One block per MOMENT, each with a label and its beats on their own
        lines. Playtest 6: "they come in these thick blocks. We need some
        formatting there so you can see what each moment actually is
        saying." A night is a dozen beats; they were one sentence.
      -->
      <ul class="story">
        <li v-for="m in story" :key="m.seq" class="story__moment" :class="`story__moment--${m.kind}`" :style="m.colour ? { '--role': m.colour } : null">
          <span class="story__label">{{ m.label }}</span>
          <!-- An act is a CARD: what was done, and what it cost and made, in
               icons (Seth and Brian, playtest 9: "clear little cards of
               action... more icons and easy to read things"; the old text
               was "flying by and there's a lot of text I'm just ignoring"). -->
          <span v-if="m.title" class="story__card">
            <strong class="story__title">{{ m.title }}</strong>
            <span v-if="m.paid.length || m.gained.length" class="story__pips">
              <Pip v-for="p in m.paid" :key="`p${p.kind}`" :kind="p.kind" :n="-p.n" signed />
              <span v-if="m.paid.length && m.gained.length" class="story__arrow" aria-hidden="true">→</span>
              <Pip v-for="p in m.gained" :key="`g${p.kind}`" :kind="p.kind" :n="p.n" signed />
            </span>
          </span>
          <ul v-if="m.lines.length" class="story__lines">
            <li v-for="(line, i) in m.lines" :key="i" class="story__line">{{ line }}</li>
          </ul>
        </li>
      </ul>
    </aside>

    <!-- dice, when something was rolled -->
    <div v-if="dice && dice.sidesOf" class="table__dice table__dice--fight">
      <!-- A fight: both sides' dice, thrown as the swords swing (playtest 12). -->
      <div v-for="side in dice.sidesOf" :key="side.who" class="table__dice-side" :class="`table__dice-side--${side.who}`">
        <span class="table__dice-label">{{ side.name }}</span>
        <span class="table__dice-faces">
          <Die3d v-for="(f, i) in side.rolls" :key="i" :value="f" :phase="dice.phase" :duration="side.each[i]" :class="{ 'die--hit': dice.phase === null && f >= side.hitOn }" />
        </span>
        <span class="table__dice-text">{{ side.result }}</span>
      </div>
    </div>
    <div v-else-if="dice" class="table__dice">
      <span class="table__dice-label">{{ dice.label }}</span>
      <span class="table__dice-faces">
        <Die3d v-for="(f, i) in dice.rolls" :key="i" :value="f" :faces="dice.sides" :phase="dice.phase" :duration="dice.each[i]" />
      </span>
      <span class="table__dice-text">{{ dice.text }}</span>
    </div>

    <!-- the night, card by card (after playtest 12): anyone moves it on -->
    <NightCards :game="game" big :can-tap="false" />
    <!-- (the old one-beat-at-a-time night, kept for old saves' logs) -->
    <div v-if="night" class="table__night" @click.stop="skipNight">
      <span class="table__night-title">Night falls on {{ monthName(night.round) }}, year {{ yearOf(night.round) }}</span>
      <ul class="table__night-lines">
        <li
          v-for="(line, i) in night.lines"
          :key="i"
          class="table__night-line"
          :class="{ 'table__night-line--shown': i < night.shown, 'table__night-line--bad': /famine|threat crept|nobody/i.test(line), 'table__night-line--also': line.startsWith('Also: ') }"
        >{{ line }}</li>
      </ul>
    </div>

    <!-- the stage: the card when the world turns, otherwise whose turn it is -->
    <div class="table__stage" :class="{ 'table__stage--card': Boolean(game.event) || isFinished || isYearEnd, 'table__stage--night': Boolean(night) }">
      <template v-if="isFinished">
        <FinishPanel :game="game" class="table__finish" />
      </template>
      <!-- THE CHRONICLE: December's night ends the year and the sitting. The
           page stays until somebody taps Done; the next sitting opens on it. -->
      <div v-else-if="isYearEnd && game.yearEnd" class="table__finish table__chronicle">
        <span class="finish__kicker">The chronicle</span>
        <h2 class="finish__title">Year {{ game.yearEnd.year }}</h2>
        <ul class="table__chronicle-lines">
          <li v-for="(line, i) in game.yearEnd.lines" :key="i">{{ line }}</li>
        </ul>
        <p class="table__muted table__stage-note">The year is over. Pack up here, or tap Done on a phone to open year {{ game.yearEnd.year + 1 }}.</p>
      </div>
      <template v-else-if="game.event">
        <EventCard :card-id="game.event.cardId" :event="game.event" :face-up="!holding" />
        <!-- Playtest 7: Brian looked to the TV for the choice; it lives on the phones. -->
        <p v-if="!game.event.resolved && !holding" class="table__stage-note table__stage-note--ask">Vote on your phones.</p>
        <!-- Who has voted for what, as it comes in (playtest 9). -->
        <ul v-if="!game.event.resolved && !holding && tableVotes.length" class="table__votes">
          <li v-for="v in tableVotes" :key="v.id"><strong>{{ v.label }}</strong> {{ v.names.join(', ') }}</li>
          <li v-if="tableWaiting.length" class="table__muted">waiting on {{ tableWaiting.join(', ') }}</li>
        </ul>
        <p v-if="game.event.resolved && !holding" class="table__muted table__stage-note">Waiting for someone to end the round.</p>
        <VoteSpin v-if="spin" table :tally="spin.tally" :picked="spin.picked" :labels="spin.labels" @close="spin = null" />
      </template>
      <!--
        The next card sits FACE DOWN beside the tally, so "flip the card"
        on the phones is a card everyone can see. Playtest 6: "the button
        says flip the card, but we can't see the card yet."
      -->
      <div v-else-if="phase === PHASES.TALLY" class="table__tally-stage">
      <EventCard v-if="game.deck.length" :card-id="game.deck[0]" :face-up="false" class="table__tally-card" />
      <div class="table__tally">
        <h3 class="table__tally-title">The day's work</h3>
        <ul class="table__tally-list">
          <li v-for="role in ROLES" :key="role" :style="{ '--role': ROLE_INFO[role].colour }">
            <strong>{{ ROLE_INFO[role].label }}</strong>
            <span v-if="!dayWork[role].net.length && !dayWork[role].notes.length" class="table__muted">nothing today</span>
            <span v-if="dayWork[role].net.length" class="table__net">
              <Pip v-for="p in dayWork[role].net" :key="p.kind" :kind="p.kind" :n="p.n" signed />
            </span>
            <span v-for="(l, i) in dayWork[role].notes" :key="i">{{ l }}</span>
          </li>
        </ul>
        <p class="table__muted table__stage-note">The card turns when everyone taps Flip<template v-if="tableWaiting.length"> - waiting on {{ tableWaiting.join(', ') }}</template>.</p>
      </div>
      </div>
      <!-- Who is still at work is the role panel's job; this only speaks up
           when nobody is (editorial pass, 2026-09-26). -->
      <div v-else-if="phase === PHASES.DAY" v-show="!stillWorking.length" class="table__spot">
        <span class="table__spot-role">Everyone is done</span>
      </div>
      <div v-else class="table__spot">
        <span class="table__spot-role">{{ date }}</span>
        <span class="table__muted">Dawn. {{ game.civ.workers }} workers have turned out</span>
        <span class="table__muted table__spot-note">{{ given }} given, {{ Math.max(0, game.civ.workers - given) }} to place</span>
      </div>
    </div>
  </main>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { mapState, mapGetters } from 'vuex';
import { PHASES, ROLE_INFO, ROLES, workersFor, tokenWord, RIVAL } from '@/engine/constants';
import { hallLeft } from '@/engine/hall';
import { rivalColour, wayOf, wayText } from '@/engine/rivals';
import {
  TECHS, REGIONS, EVENTS, GOALS, goalOf, BRANCHES, TALES, YEAR_GOALS,
} from '@/engine/cards';
import { dateOf, monthName, yearOf, seasonName } from '@/engine/calendar';
import { YEAR_END } from '@/engine/game';
import { allocationOf, handsOf, dayLog, foodEatenPerRound, logTotal, waitingOn } from '@/engine/game';
import { demandWhen } from '@/engine/demands';
import ResourceStrip from './ResourceStrip.vue';
import EventCard from './EventCard.vue';
import FinishPanel from './FinishPanel.vue';
import DeckStack from './DeckStack.vue';
import { victoryProgress, rivalProgress } from '@/engine/victory';
import { asksOf } from '@/engine/asks';
import Compass from './Compass.vue';
import VoteSpin from '@/components/shared/VoteSpin.vue';
import RolePeek from '@/components/shared/RolePeek.vue';
import Die3d from './Die3d.vue';
import Pip from './Pip.vue';
import { SPENDABLE } from './resourceArt';
import { prefersReducedMotion } from '@/utils/eventTheatre';
import {
  governmentOf, GOVERNMENTS, describeCompass, compassOf, councilOf, COUNSELORS,
} from '@/engine/hall';
import { PRINCIPLE_NAMES, describeNature } from '@/engine/lore';
import { eventHoldMs } from '@/utils/eventTheatre';
import { captionFor } from '@/world/captions';
import { shotFor } from '@/world/choreography';
import NightCards from './NightCards.vue';
import { unitName } from '@/engine/units';

// The tally's pips, in the order every cost rail and every ledger line uses.
const PIP_ORDER = [...SPENDABLE.filter((k) => k !== 'workers'), 'population', 'happiness', 'threat'];

const CAPTION_MS = 7000;
const NIGHT_BEAT_MS = 1400;
const DICE_MS = 4500;

/** The upkeep log line, split into beats to be read one at a time. */
/**
 * An act's sentence, split where it is safe to: at "; " (the paid/gained
 * seam) and before a beat that starts with "the " or a capital. A list of
 * properties ("hot, wet, and heavy") stays together.
 */
export const actBeats = (body) => body
  .split(/; |, (?=(?:the |a |an |[A-Z]))/)
  .map((b) => b.trim())
  .filter(Boolean)
  .map((b) => b[0].toUpperCase() + b.slice(1));

// How long the night's summary stays up after its last line.
const NIGHT_READ_MS = 25000;

/**
 * The night, SHORT (Matt, playtest 2026-09-25: "that's too much for us to
 * read and it's gonna go away before I can read it - make that nightfall
 * summary more succinct"). What changes the table's plans is kept word for
 * word - losses, raiders, fires, arrivals, finds, a card, the race - and
 * the routine (eating, fading, workers turning out) is folded into one
 * closing line. The full night stays in "the story so far".
 */
const NOTABLE = /and got|burn|sack|take|raid|famine|starv|die|dead|lost|retire|arriv|born|famil|found|chair|moot|council|wonder|finish|race|joined|hostile|friendly|war party|camp|den|fever|sick|revolt|broken|eureka|theory|charter|debunk|settled|gift|carts|trade/i;
export const nightHeadlines = (beats) => {
  const keep = beats.filter((b) => NOTABLE.test(b));
  const rest = beats.length - keep.length;
  const out = keep.slice(0, 5);
  const more = rest + Math.max(0, keep.length - 5);
  // Say what the rest IS - the actual lines, short (playtest 10: "I still
  // don't know what it means when it says ...and X more"; playtest 11, on
  // "3 smaller things (eating, fading, workers)": "I don't have any idea
  // what those three smaller things were").
  if (more > 0) {
    const dropped = [...beats.filter((b) => !keep.includes(b)), ...keep.slice(5)];
    // Cut at a word that can end a phrase: "Happiness over 6 fades a" read
    // as garbage (playtest 12), "Happiness over 6 fades" does not.
    const DANGLING = /^(a|an|the|of|for|to|and|from|by|in|on|at|with|over|every|each|who|that)$/i;
    const short = (b) => {
      const words = b.replace(/[:(].*$/, '').trim().split(' ').slice(0, 5);
      while (words.length > 1 && DANGLING.test(words[words.length - 1])) words.pop();
      return words.join(' ');
    };
    out.push(`Also: ${dropped.map(short).join(' · ')}`);
  }
  return out.length ? out : beats.slice(0, 3);
};

export const nightBeats = (text) => {
  const body = text.replace(/^End of round \d+: /, '').replace(/\.$/, '');
  // Break at EVERY comma, then put back the pieces that only continue the
  // one before ("-1 happiness, now 6", "rolled 6, needed 5", "hot, wet,
  // light"). Splitting only before words it expected left "raiders come
  // out..., wolves come out..., wolves sack..." as one line (playtest
  // 2026-09-25: "some of the lines still get way too long").
  const CONT = /^(now |needed |and |then |or |hot|cold|wet|dry|heavy|light|[+-]\d|\d+ (keep|left)\b)/i;
  const parts = body.split(/, /);
  const out = [];
  parts.forEach((p) => {
    if (out.length && CONT.test(p)) out[out.length - 1] += `, ${p}`;
    else out.push(p);
  });
  return out.map((b) => b.trim()).filter(Boolean).map((b) => b[0].toUpperCase() + b.slice(1));
};

export default {
  name: 'TableView',
  components: {
    ResourceStrip,
    Pip,
    Die3d,
    EventCard,
    FinishPanel,
    DeckStack,
    Compass,
    VoteSpin,
    RolePeek,
    // three.js only ever ships to the table; phones never download it.
    NightCards,
    WorldScene: defineAsyncComponent(() => import('@/world/WorldScene.vue')),
  },
  data() {
    return {
      // A split vote that just landed, being shown.
      spin: null,
      northAngle: null,
      // Nameplates over the peoples' homes, placed from the camera.
      plates: [],
      // Another role's day, opened from its tile (playtest 9).
      peekRole: null,
      PHASES, ROLE_INFO, ROLES, TECHS, REGIONS, BRANCHES, GOALS, TALES,
      // The drama: the card sits face down for a beat, and the numbers up
      // top AND the world keep showing the commonwealth BEFORE the card
      // until it turns. The engine has already applied it; the table simply
      // hasn't said so.
      holding: false,
      heldCiv: null,
      holdTimer: null,
      // Tap the island, get told what that is.
      caption: null,
      captionAt: { x: 0, y: 0 },
      captionTimer: null,
      // The end of the round, one beat at a time: { round, lines, shown }.
      night: null,
      nightTimers: [],
      dice: null,
      diceTimer: null,
    };
  },
  computed: {
    tableWaiting() {
      return waitingOn(this.game).map((id) => (this.game.players.find((p) => p.id === id)?.name || 'someone').split(' ')[0]);
    },
    tableVotes() {
      const votes = this.game.event?.votes || {};
      const labels = this.choiceLabels();
      const byChoice = {};
      Object.entries(votes).forEach(([id, c]) => {
        (byChoice[c] = byChoice[c] || []).push((this.game.players.find((p) => p.id === id)?.name || 'someone').split(' ')[0]);
      });
      return Object.entries(byChoice).map(([c, names]) => ({ id: c, label: labels[c] || c, names }));
    },
    logCount() { return logTotal(this.game); },
    victories() { return victoryProgress(this.shownCiv); },
    victoryLine() { return this.victories.map((v) => `${v.name} ${v.have} of ${v.need}`).join(' · '); },
    asks() { return asksOf(this.game); },
    rivalRace() { return rivalProgress(this.shownCiv).filter((r) => r.have > 0); },
    yearGoal() { const g = this.shownCiv.yearGoal; return g && YEAR_GOALS[g.id] ? `${YEAR_GOALS[g.id].name}${g.met === true ? ' - met' : g.met === false ? ' - missed' : ''}` : ''; },
    // At the year's end the round has already advanced; the page is December's until Done.
    date() { return dateOf(this.isYearEnd ? this.game.meta.round - 1 : this.game.meta.round); },
    seasonWord() { return seasonName(this.isYearEnd ? this.game.meta.round - 1 : this.game.meta.round); },
    isYearEnd() { return this.game.meta.phase === YEAR_END; },
    eatsTonight() { return foodEatenPerRound(this.shownCiv); },
    // What the people were promised, and how long until they ask - never a
    // month by name (Matt: "if it shows up in April, that's going to be like
    // we lied to them").
    demandLine() {
      const d = this.game.civ.demand;
      return d ? { label: d.label, when: demandWhen(d, this.game.meta.round) } : null;
    },
    ...mapState(['game']),
    ...mapGetters(['isFinished', 'playerName']),
    phase() { return this.game.meta.phase; },
    shownCiv() { return (this.holding && this.heldCiv) || this.game.civ; },
    allocation() { return allocationOf(this.game); },
    hands() { return handsOf(this.game); },
    given() { return ROLES.reduce((n, r) => n + this.allocation[r], 0); },
    stillWorking() { return ROLES.filter((r) => !this.game.done?.[r]); },
    // The tally: what each role's day came to - net resources, and the
    // few things worth naming - not every line (Matt: "too literal").
    dayWork() {
      const out = Object.fromEntries(ROLES.map((r) => [r, { net: [], notes: [] }]));
      const nets = Object.fromEntries(ROLES.map((r) => [r, {}]));
      const notes = Object.fromEntries(ROLES.map((r) => [r, []]));
      const NAMED = [
        [/EUREKA - that is the reaction of ([^;]+); it is learned/, (m) => `learned ${m[1]}`],
        [/found ([A-Z][^,]+)/, (m) => `found ${m[1]}`],
        [/the (\w+) is cleared/, (m) => `cleared a ${m[1]}`],
        [/stage (\d+) of (\d+)/, (m) => `raised stage ${m[1]} of ${m[2]}`],
        [/a (\w+) stands at/, (m) => `built a ${m[1]}`],
        [/the city takes in/, () => 'annexed a hex'],
        [/stands as law/, () => 'a law stands'],
        [/adds ([^.;,]+) to the deck/, (m) => `bought ${m[1]}`],
        [/publishes: ([^-]+) -/, (m) => `published: ${m[1].trim()}`],
        [/takes a chair/, () => 'seated a counselor'],
        [/^(.+?) lapses, for good/, (m) => `trashed ${m[1]}`],
        [/takes on (.+?) for/, (m) => `took on ${m[1]}`],
        [/(\w+(?: of \d+)?) is destroyed/, (m) => `${m[1]} destroyed`],
        // Playtest 6: "it often said the scholar did nothing even though
        // they did achieve things." Mixing, aiming and consulting the
        // stones move no resource, so they need naming.
        [/mixes (\w+) and (\w+): (.+?)(?:,|$)/, (m) => `mixed ${m[1]} and ${m[2]}: ${m[3]}`],
        [/aims \w+ and \w+ at ([^-]+?) - the reaction fails/, (m) => `tried ${m[1].trim()}: not it`],
        [/the stones say: ([^,]+)/, (m) => `the stones: ${m[1]}`],
        [/the theory holds/, () => 'a theory held'],
        [/gives (.+?) to (.+?) face down: (.+?)(?:,|$)/, (m) => `${m[2]}: ${m[3]}`],
        [/moves to ([^,]+)/, (m) => `moved to ${m[1]}`],
        [/a village stands at ([^,]+)/, (m) => `founded a village at ${m[1]}`],
        [/breaks up/, () => 'a camp broke up'],
        // A worker handed across the table is named on both sides (playtest
        // 14: "didn't feel like you were getting stuff" from the other roles).
        [/(\d+) (?:worker goes|workers go) to the (\w+)/, (m) => `sent ${m[1]} to the ${m[2]}`],
        [/the Hall is dealt (\d+) cards? for them/, (m) => `${m[1]} cards for the Hall`],
      ];
      const RECEIVED = /(\d+) (?:worker goes|workers go) to the (Hall|Stewards|Scholars|Frontier)/g;
      dayLog(this.game).forEach((l) => {
        const r = l.role;
        if (!nets[r]) return;
        const body = l.text.replace(/^.*? - /, '');
        // The act carries what it cost and what it gave, so the tally adds
        // those up rather than reading its own sentence back. (An old save's
        // entries predate the parts; those still fall back to the prose.)
        if (l.gained || l.paid) {
          Object.entries(l.gained || {}).forEach(([k, n]) => { nets[r][k] = (nets[r][k] || 0) + n; });
          Object.entries(l.paid || {}).forEach(([k, n]) => { nets[r][k] = (nets[r][k] || 0) - n; });
        } else {
          [...body.matchAll(/([+-]\d+) (food|goods|coin|knowledge|happiness|population)\b/g)].forEach((m) => { nets[r][m[2]] = (nets[r][m[2]] || 0) + Number(m[1]); });
          const paid = /paid ([^;]+);/.exec(body);
          if (paid) [...paid[1].matchAll(/(\d+) (food|goods|coin|knowledge)\b/g)].forEach((m) => { nets[r][m[2]] = (nets[r][m[2]] || 0) - Number(m[1]); });
        }
        NAMED.forEach(([re, f]) => { const m = re.exec(body); if (m) notes[r].push(f(m)); });
        [...body.matchAll(RECEIVED)].forEach((m) => {
          const to = ROLES.find((x) => ROLE_INFO[x].label === m[2]);
          if (to && to !== r) notes[to].push(`+${m[1]} from the ${ROLE_INFO[r].label}`);
        });
      });
      // Workers are the day itself, not a thing the day produced: every
      // action spends them, so a tally of "-6 workers" says nothing.
      ROLES.forEach((r) => {
        out[r] = {
          net: PIP_ORDER
            .filter((k) => nets[r][k])
            .map((k) => ({ kind: r === 'trade' && k === 'workers' ? 'shifts' : k, n: nets[r][k] })),
          notes: notes[r].slice(0, 4),
        };
      });
      return out;
    },
    goal() { return GOALS[goalOf(this.shownCiv)] || null; },
    raisedWonders() {
      return (this.shownCiv.wonders || []).map((w) => GOALS[w]).filter(Boolean).map((g) => {
        const t = g.text || '';
        const gives = t.split('Finished, ')[1] || t.split(': ').slice(1).join(': ') || t;
        return { name: g.name, gives: gives.replace(/^\w/, (c) => c.toUpperCase()) };
      });
    },
    peoples() {
      const until = (age, every) => every - ((age || 0) % every);
      const nights = (n) => (n === 1 ? 'tomorrow night' : `in ${n} nights`);
      return (this.shownCiv.rivals || []).filter((r) => r.met).map((r) => {
        const way = wayOf(r);
        const next = [];
        if (r.stance === 'hostile') {
          next.push(`They may send raiders any night; demands come ${nights(until(r.age, RIVAL.demandEvery))}.`);
        } else if (r.stance === 'friendly') {
          next.push(`Carts come ${nights(until(r.age, way.tradeEvery))}: ${Object.entries(way.trade).map(([k, n]) => `+${n} ${k}`).join(', ')}.`);
        } else if (way.demandsWhenWary) {
          next.push(`Demands come ${nights(until(r.age, RIVAL.demandEvery))}. A gift warms them.`);
        } else {
          next.push('Watching. A gift, an Envoy or a parley warms them; a raid turns them.');
        }
        if ((r.held || []).length < way.maxHeld) next.push(`They take new ground ${nights(until(r.age, way.expandEvery))}.`);
        return {
          id: r.id, name: r.name.replace(/^the /, 'The '), stance: r.stance, power: r.power, colour: rivalColour(r),
          way: wayText(r), next: next.join(' '),
        };
      });
    },
    unmetPeoples() { return (this.shownCiv.rivals || []).filter((r) => !r.met).length; },
    governmentName() { const g = governmentOf(this.shownCiv); return g ? GOVERNMENTS[g].name : 'no government yet'; },
    compassText() { return describeCompass(compassOf(this.shownCiv)); },
    councilNames() { return councilOf(this.shownCiv).map((c) => COUNSELORS[c.id]?.name).filter(Boolean).join(', '); },
    settledText() { return Object.entries(this.shownCiv.lore?.settled || {}).map(([p, n]) => `${PRINCIPLE_NAMES[p]} is ${describeNature(n)}`).join('; '); },
    // During the hold the card is off the deck but not yet turned; the
    // count keeps pace with the world, not the engine.
    shownDeckCount() { return this.holding ? this.game.deck.length + 1 : this.game.deck.length; },
    captionStyle() {
      const el = this.$el;
      const w = el?.clientWidth || window.innerWidth;
      const h = el?.clientHeight || window.innerHeight;
      const left = Math.min(Math.max(this.captionAt.x, 180), w - 180);
      const above = this.captionAt.y > h * 0.4;
      return { left: `${left}px`, top: above ? 'auto' : `${this.captionAt.y + 16}px`, bottom: above ? `${h - this.captionAt.y + 16}px` : 'auto' };
    },
    headline() {
      if (this.isFinished) return this.game.result?.won ? 'The commonwealth has won.' : 'The commonwealth has fallen.';
      if (this.isYearEnd) return `Year ${this.game.yearEnd?.year || yearOf(this.game.meta.round - 1)} is over`;
      if (this.phase === PHASES.DAWN) return 'Dawn: dividing the workers';
      if (this.phase === PHASES.DAY) return 'The day';
      if (this.phase === PHASES.TALLY) return 'The day is done';
      if (this.holding) return 'The world turns…';
      const card = EVENTS[this.game.event?.cardId];
      return card ? card.name : 'The event';
    },
    recentLog() { return this.game.log.slice(-10).reverse(); },
    // The latest few technologies, newest first; the count says the rest.
    knownRecent() {
      const names = this.shownCiv.techs.slice(-4).reverse().map((t) => TECHS[t]?.name).filter(Boolean);
      return this.shownCiv.techs.length > 4 ? `${names.join(', ')}, …` : names.join(', ');
    },
    // The story so far as MOMENTS: a label (who, or what turned, or which
    // night) and the beats under it, one to a line.
    story() {
      // Back as far as the game keeps (playtest 11, Matt: "you can't scroll
      // back far enough, I wish I could scroll back all the way"); the
      // column scrolls, so only the newest are on screen at first.
      return this.game.log.slice(-120).reverse().map((l, i) => {
        const seq = l.seq ?? `${this.game.log.length}-${i}`;
        if (l.kind === 'upkeep') {
          const m = /^End of round (\d+)/.exec(l.text);
          return { seq, kind: l.kind, label: m ? `Night · ${monthName(Number(m[1]))}` : 'Night', lines: nightBeats(l.text) };
        }
        if (l.kind === 'event') {
          const m = /^The world turns: (.+?)\. (.*)$/s.exec(l.text);
          return { seq, kind: l.kind, label: m ? m[1] : 'The world turns', lines: [m ? m[2] : l.text] };
        }
        if (l.kind === 'act' && l.role) {
          const body = l.text.replace(/^.*? - /, '').replace(/\.$/, '');
          const who = this.playerName(this.game.roles[l.role]);
          const pips = (patch) => [...SPENDABLE, ...PIP_ORDER].filter((k, j, all) => all.indexOf(k) === j && patch?.[k]).map((k) => ({ kind: k, n: patch[k] }));
          const paid = pips(l.paid);
          const gained = pips(l.gained);
          // With the numbers in icons, the words keep only what they add.
          const lines = actBeats(body).filter((b) => !/(^|: )paid \d/i.test(b) && !(gained.length && /^[+-]?\d/.test(b)));
          return {
            seq, kind: l.kind, colour: ROLE_INFO[l.role]?.colour,
            label: who ? `${ROLE_INFO[l.role]?.label} · ${who}` : ROLE_INFO[l.role]?.label,
            title: l.label || '', paid, gained, lines: lines.slice(0, 2),
          };
        }
        if (l.kind === 'choice') return { seq, kind: l.kind, label: 'The table decided', lines: [l.text.replace(/\.$/, '')] };
        if (l.kind === 'wonder') return { seq, kind: l.kind, label: 'A wonder stands', lines: [l.text] };
        if (l.kind === 'season') return { seq, kind: l.kind, label: 'A new deal', lines: [l.text] };
        if (l.kind === 'year') return { seq, kind: l.kind, label: 'The chronicle', lines: [l.text] };
        if (l.kind === 'end') return { seq, kind: l.kind, label: 'The end', lines: [l.text] };
        return { seq, kind: l.kind, label: '', lines: [l.text] };
      });
    },
  },
  watch: {
    'game.event.vote': function onVote(now, was) {
      if (!now || was || !now.split) return;
      this.spin = { tally: now.tally, picked: now.picked, labels: this.choiceLabels() };
    },
    'game.meta.round': {
      handler(now, was) {
        // The night is read as cards now (NightCards); the old beats run
        // only for a night written before the cards existed.
        if (was && now > was && !this.game.nightRead) this.beginNight(was);
      },
    },
    'game.meta.phase': {
      handler(now) { if (now !== PHASES.DAWN && this.night) this.skipNight(); },
    },
    // Something happened: point the camera at it.
    // logTotal, not log.length: the log is trimmed in a long game, so its
    // length stops growing; the total never does.
    logCount: {
      handler(now, was) {
        // `!was` also covers a reload, where the whole log lands at once:
        // those dice were thrown long ago and must not roll again.
        if (!was || now <= was) return;
        const entry = this.game.log[this.game.log.length - 1];
        const shot = shotFor(entry, this.game.civ);
        if (shot) this.$refs.world?.focus(shot);
        // Dice on the table: what was rolled, where the camera went. A fight's
        // dice wait for the clash the world is staging, and show both sides.
        const battle = shot?.battle;
        if (battle) {
          window.clearTimeout(this.diceTimer);
          window.clearTimeout(this.settleTimer);
          window.clearTimeout(this.clashTimer);
          const beats = this.$refs.world?.battleBeats?.() || { clash: 0, swing: 2100 };
          const named = unitName({ id: battle.ours.id });
          const ourName = named.charAt(0).toUpperCase() + named.slice(1);
          const theirName = battle.theirs.kind === 'rival' ? 'They' : battle.theirs.kind === 'raiders' ? 'Raiders' : `The ${battle.theirs.kind}`;
          const hits = (side) => side.dice.filter((f) => f >= side.hitOn).length;
          const result = (side, other) => {
            const n = hits(other);
            const left = side.after;
            return left <= 0 ? `${n} ${n === 1 ? 'hit' : 'hits'} taken - gone` : `${n} ${n === 1 ? 'hit' : 'hits'} taken - ${left} left`;
          };
          this.clashTimer = window.setTimeout(() => {
            const sidesOf = [
              { who: 'ours', name: ourName, rolls: battle.ours.dice, hitOn: battle.ours.hitOn, result: result(battle.ours, battle.theirs) },
              { who: 'theirs', name: theirName, rolls: battle.theirs.dice, hitOn: battle.theirs.hitOn, result: result(battle.theirs, battle.ours) },
            ].map((x) => ({ ...x, each: x.rolls.map(() => 700 + Math.round(Math.random() * 700)) }));
            this.dice = { sidesOf, phase: null };
            if (!prefersReducedMotion()) this.$nextTick(() => { if (this.dice) this.dice = { ...this.dice, phase: 'tumble' }; });
            const longest = Math.max(...sidesOf.flatMap((x) => x.each));
            this.settleTimer = window.setTimeout(() => { if (this.dice) this.dice = { ...this.dice, phase: null }; }, longest + 150);
            this.diceTimer = window.setTimeout(() => { this.dice = null; }, longest + beats.swing + 2600);
          }, Math.max(0, beats.clash - 300));
        } else if (entry.rolls) {
          window.clearTimeout(this.diceTimer);
          window.clearTimeout(this.settleTimer);
          // Every die its own length, so they land one after another
          // rather than all at once - the house dice animation's own rule.
          const each = entry.rolls.map(() => 900 + Math.round(Math.random() * 800));
          // At rest for one tick, then thrown: Die3d tumbles on a phase
          // CHANGE, and these are created fresh with every roll.
          this.dice = {
            rolls: entry.rolls,
            sides: entry.sides,
            each,
            phase: null,
            label: entry.action === 'fight' ? 'Fight' : entry.action === 'hunt' ? 'Hunt' : 'Roll',
            text: entry.text.replace(/^.*? - /, ''),
          };
          if (!prefersReducedMotion()) {
            this.$nextTick(() => { if (this.dice) this.dice = { ...this.dice, phase: 'tumble' }; });
          }
          this.settleTimer = window.setTimeout(() => {
            if (this.dice) this.dice = { ...this.dice, phase: null };
          }, Math.max(...each) + 150);
          this.diceTimer = window.setTimeout(() => { this.dice = null; }, DICE_MS);
        }
      },
    },
    'game.event': {
      handler(now, was) {
        if (now && !was) {
          const ms = eventHoldMs();
          if (ms > 0) {
            this.heldCiv = this.lastCiv || this.game.civ;
            this.holding = true;
            window.clearTimeout(this.holdTimer);
            this.holdTimer = window.setTimeout(() => { this.holding = false; }, ms);
          }
        }
        if (!now) this.holding = false;
      },
    },
    'game.civ': {
      handler(now, was) {
        // Remember the civ as it stood before the latest change, so the
        // hold (and the night) have something honest to show.
        if (this.phase !== PHASES.EVENT) this.lastCiv = was || now;
      },
      immediate: true,
    },
  },
  mounted() {
    this.northTimer = window.setInterval(() => {
      // Where the resource strip ends, so the turned card hangs below it
      // (playtest y03oc: the card covered the numbers on smaller screens).
      const strip = this.$el.querySelector?.('.table__resources');
      const below = strip ? Math.round(strip.getBoundingClientRect().bottom) : 0;
      if (below && below !== this.stripBottom) {
        this.stripBottom = below;
        this.$el.style.setProperty('--strip-bottom', `${below}px`);
      }
      const a = this.$refs.world?.azimuth?.();
      this.northAngle = typeof a === 'number' ? a : null;
      const world = this.$refs.world;
      const seen = new Set(this.shownCiv.seen || []);
      this.plates = (this.shownCiv.rivals || [])
        .filter((r) => r.met && (r.held || []).some((k) => seen.has(k)))
        .map((r) => {
          const at = world?.screenOfHex?.(r.q, r.r);
          // Never up under the resource bar.
          return at ? {
            id: r.id, x: at.x, y: Math.max(at.y, 300), stance: r.stance, colour: rivalColour(r), name: r.name.replace(/^the /, 'The '),
          } : null;
        })
        .filter(Boolean);
    }, 120);
  },
  beforeUnmount() {
    window.clearInterval(this.northTimer);
    window.clearTimeout(this.holdTimer);
    window.clearTimeout(this.captionTimer);
    window.clearTimeout(this.clashTimer);
    this.nightTimers.forEach((t) => window.clearTimeout(t));
  },
  methods: {
    choiceLabels() {
      const card = EVENTS[this.game.event?.cardId];
      return Object.fromEntries((this.game.event?.choices || card?.choices || []).map((c) => [c.id, c.label]));
    },
    monthName,
    yearOf,
    rivalColour,
    // Tokens back to people: the Stewards place half-days, but a tab that
    // counts heads must count heads.
    workersFor,
    tokenWord,
    hallLeft,
    /**
     * The round just ended. Hold the world as it was and read out what
     * the night did, one line at a time, before the new day starts.
     * Playtest 3: "it all goes a bit quickly... very clinically."
     */
    beginNight(endedRound) {
      const entry = [...this.game.log].reverse().find((l) => l.kind === 'upkeep');
      if (!entry) return;
      const lines = nightHeadlines(nightBeats(entry.text));
      const beat = eventHoldMs() > 0 ? NIGHT_BEAT_MS : 0;
      this.nightTimers.forEach((t) => window.clearTimeout(t));
      this.nightTimers = [];
      if (beat === 0) return;
      if (this.lastCiv) { this.heldCiv = this.lastCiv; this.holding = true; }
      this.night = { round: endedRound, lines, shown: 0 };
      lines.forEach((line, i) => {
        this.nightTimers.push(window.setTimeout(() => {
          if (!this.night) return;
          this.night.shown = i + 1;
          // The numbers catch up with each beat that changes them.
          if (i === lines.length - 1) this.holding = false;
        }, beat * (i + 1)));
      });
      // The night used to stay until a tap or the start of the day ("it
      // clears itself before I can read it"). But a TV in another city has
      // nobody to tap it, and it sat over the dawn (playtest 7: "it'd be
      // nice if we could dismiss it and be presented with the dawn"). So it
      // stays for a good read - NIGHT_READ_MS after its last line - and then
      // gives the stage to the dawn. A tap still clears it sooner.
      this.nightTimers.push(window.setTimeout(() => { if (this.night) this.skipNight(); }, beat * lines.length + NIGHT_READ_MS));
    },
    skipNight() {
      this.nightTimers.forEach((t) => window.clearTimeout(t));
      this.nightTimers = [];
      this.night = null;
      this.holding = false;
    },
    skipHold() {
      if (!this.holding) return;
      window.clearTimeout(this.holdTimer);
      this.holding = false;
    },
    onPick({ placement, x, y }) {
      const rect = this.$el?.getBoundingClientRect?.() || { left: 0, top: 0 };
      window.clearTimeout(this.captionTimer);
      const caption = captionFor(placement, this.shownCiv);
      if (!caption) { this.caption = null; return; }
      this.caption = caption;
      this.captionAt = { x: x - rect.left, y: y - rect.top };
      this.captionTimer = window.setTimeout(() => { this.caption = null; }, CAPTION_MS);
    },
    // Playtest 7: "I don't have any idea what threat means."
    explainTrack(key) {
      if (key !== 'threat') return;
      const n = (this.shownCiv.menaces || []).length;
      this.caption = {
        title: `Threat ${this.shownCiv.threat || 0}`,
        text: `The strength of every camp and den you know of, added up (${n} out there now). Each night a camp may send raiders at your nearest village or the city: three reaching a village burn it, and at the city each one fires a building. A unit standing in their way stops them. Clear or parley camps to bring it down.`,
      };
      this.captionAt = { x: (this.$el?.clientWidth || 1200) / 2, y: 140 };
      window.clearTimeout(this.captionTimer);
      this.captionTimer = window.setTimeout(() => { this.caption = null; }, CAPTION_MS * 2);
    },
  },
};
</script>
